import React, { useEffect, useState } from "react";
import {
  Plus,
  FileText,
  Layers,
  GripVertical,
  Copy,
  X,
  Check,
  Upload,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ContentSectionEditor from "./components/ContentSectionEditor";

const SortableSection = ({ section, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 0,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? "shadow-2xl" : ""}`}
    >
      <div className="absolute -left-12 top-2 flex flex-col gap-2 items-center">
        <div
          {...attributes}
          {...listeners}
          className="p-2 hover:text-primary bg-white shadow-sm border border-slate-200 rounded-lg cursor-grab active:cursor-grabbing transition-all"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <button
          onClick={onDelete}
          className="p-2 hover:text-red-500 bg-white shadow-sm border border-slate-200 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
        >
          <Plus className="w-4 h-4 rotate-45" />
        </button>
      </div>
      <ContentSectionEditor section={section} onUpdate={onUpdate} />
    </div>
  );
};

const Editor = ({ contentData, setContentData, read }) => {
  const [imported, setImported] = useState(false);
  const [sections, setSections] = useState([
    {
      id: crypto.randomUUID(),
      title: "Introduction",
      headingTag: "h2",
      blocks: [
        {
          id: crypto.randomUUID(),
          type: "rich-text",
          content: "<p>Start editing this block.</p>",
        },
      ],
    },
  ]);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState("");
  const [copied, setCopied] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addSection = () => {
    setSections([
      ...sections,
      {
        id: crypto.randomUUID(),
        title: "",
        headingTag: "h3",
        blocks: [],
      },
    ]);
  };

  const updateSection = (id, updates) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    );
  };

  const deleteSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    setContentData(sections);
  }, [sections]);
  useEffect(() => {
    console.log("contantData", contentData);

    if (contentData && contentData.length > 0) {
      handleImport();
    }
  }, [contentData]);
  const handleImport = () => {
    try {
      if (!imported) {
        // const data = JSON.parse(contantData);
        if (Array.isArray(contentData)) {
          setSections(contentData);
          setImported(true);
        }
      }
    } catch (e) {
      alert("Invalid JSON format. Please check your data.");
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <div className="w-64 bg-white border-r border-slate-200 flex-col hidden md:flex h-full fixed  z-40 shadow-sm">
        <div className="p-6 border-b border-slate-100 flex items-center gap-2">
          <div className="bg-indigo-600 p-1.5 rounded-lg">
            <Layers className="w-5 h-5 text-white" />
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 px-2">
            Outline
          </div>
          {sections.map((section, idx) => (
            <button
              key={section.id}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-100 transition-colors flex items-center gap-3 text-sm font-medium text-slate-600 group"
              onClick={() =>
                document
                  .getElementById(`section-${section.id}`)
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <FileText className="w-4 h-4 text-slate-400 group-hover:text-indigo-500" />
              <span className="truncate">
                {section.title || `Untitled ${idx + 1}`}
              </span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100 space-y-3 bg-slate-50/50">
          <button
            onClick={() => setShowImportModal(true)}
            className="w-full flex items-center justify-center gap-2 bg-white text-slate-600 py-2.5 rounded-xl font-semibold hover:bg-slate-50 transition-all border border-slate-200 shadow-sm"
          >
            <Upload className="w-4 h-4" /> Import JSON
          </button>
          <button
            onClick={addSection}
            className="w-full flex items-center justify-center gap-2 bg-indigo-50 text-indigo-700 py-2.5 rounded-xl font-semibold hover:bg-indigo-100 transition-all border border-indigo-200 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Section
          </button>
        </div>
      </div>

      <main className="flex-1 pb-20">
        <header className="bg-white/80  border-slate-200 px-8 py-4 flex items-center justify-between rounded-[4px]">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-slate-800">
              Content Engine
            </h1>
            <span className="px-2 py-0.5 bg-[#D4AD3015] text-primary text-[10px] font-bold rounded-full uppercase tracking-widest border border-primary">
              Editor
            </span>
          </div>
          {/* <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowPublishModal(true)}
                            className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                        >
                            <Save className="w-4 h-4" /> Publish
                        </button>
                    </div> */}
        </header>

        <div className="max-w-7xl mx-auto py-12 px-8">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-12">
                {sections.map((section) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    onUpdate={(upd) => updateSection(section.id, upd)}
                    onDelete={() => deleteSection(section.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {!read && (
          <button
            onClick={addSection}
            type="button"
            className="w-full max-w-[30rem] mx-auto flex items-center justify-center gap-2 bg-[#D4AD3015] text-primary py-2.5 rounded-xl font-semibold hover:bg-[#D4AD3015] transition-all border border-primary shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Section
          </button>
        )}
      </main>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowPublishModal(false)}
          />
          <div className="relative bg-white w-full max-w-3xl rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Export Configuration</h2>
              <button onClick={() => setShowPublishModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 bg-slate-950 font-mono text-xs text-indigo-400">
              <pre>{JSON.stringify(sections, null, 2)}</pre>
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3 justify-end">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(sections));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className={`flex items-center gap-2 px-8 py-2.5 rounded-xl font-bold transition-all ${copied ? "bg-green-600 text-white" : "bg-indigo-600 text-white"}`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}{" "}
                {copied ? "Copied" : "Copy JSON"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          />
          <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl flex flex-col animate-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Import Content State</h2>
              <button onClick={() => setShowImportModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                className="w-full h-64 bg-slate-50 border border-slate-200 rounded-xl p-4 font-mono text-sm focus:ring-2 focus:ring-indigo-500/20 focus:outline-none"
                placeholder="Paste your JSON here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3 justify-end">
              <button
                onClick={handleImport}
                className="bg-indigo-600 text-white px-8 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all"
              >
                Hydrate State
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
